/**
 * Admin Analytics — reported client errors + their triage.
 *
 * The "Report" action (errorToast / ErrorBoundary) beacons to POST
 * /client-errors, which the backend persists. This reads them back for the
 * admin "Reported errors" tab, and drives triage: assign it, move it, reply.
 *
 * Assignment and status are NOT optimistic. They are appended events whose
 * current state the server derives, and the server rejects some of them (a
 * status move on an unassigned report is 409) — painting the new state before
 * the server agrees would show an admin a history that did not happen.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import {
  adminApi,
  type ClientErrorReportsResponse,
  type ReportTriageResponse,
  type TriageEventRequest,
} from '@/api/endpoints/admin';
import { errorToast } from '@/hooks/errorToast';
// i18next directly, NOT '@/i18n' — that module's load side-effect runs
// i18n.init(), which breaks any suite mocking react-i18next without
// initReactI18next. Same dodge errorToast.tsx documents.
import i18n from 'i18next';

export function useClientErrors(limit = 100, offset = 0) {
  return useAuthenticatedQuery<ClientErrorReportsResponse>({
    queryKey: ['admin', 'client-errors', limit, offset],
    queryFn: () => adminApi.getClientErrors(limit, offset),
    staleTime: 30_000,
  });
}

const triageKey = (reportId: string) => ['admin', 'client-errors', 'triage', reportId];

/**
 * One report's triage state. `enabled` so a collapsed card costs nothing — the
 * list renders every report, but only an opened one needs its history.
 */
export function useClientErrorTriage(reportId: string, enabled: boolean) {
  return useAuthenticatedQuery<ReportTriageResponse>({
    queryKey: triageKey(reportId),
    queryFn: () => adminApi.getClientErrorTriage(reportId),
    enabled,
    staleTime: 10_000,
  });
}

export function useAppendTriageEvent(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TriageEventRequest) => adminApi.postClientErrorEvent(reportId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: triageKey(reportId) });
    },
    onError: (err) => {
      errorToast({
        context: 'triageEvent',
        description: i18n.t('admin.reportedErrors.triage.eventFailed'),
        error: err,
      });
    },
  });
}

export function usePostTriageComment(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => adminApi.postClientErrorComment(reportId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: triageKey(reportId) });
    },
    onError: (err) => {
      errorToast({
        context: 'triageComment',
        description: i18n.t('admin.reportedErrors.triage.replyFailed'),
        error: err,
      });
    },
  });
}
